import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Compass, Target, Timer, Layers, AlertCircle, CheckCircle2, Trophy,
  Circle, ArrowRight, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MasterySkillDetail — the deep view for a single skill.
 * Shows all 7 sections with layered checklist, mastery CTA, and "Agree next skill".
 */
export default function MasterySkillDetail() {
  const { skillId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/mastery/skills/${skillId}`, { withCredentials: true });
      setData(data);
    } catch {
      toast.error("Couldn't load skill.");
    }
  };

  useEffect(() => { load(); }, [skillId]);

  const startSkill = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/mastery/skills/${skillId}/start`, {}, { withCredentials: true });
      toast.success("Started — one layer at a time.");
      await load();
    } finally { setBusy(false); }
  };

  const toggleLayer = async (idx, current) => {
    // Optimistic update
    setData((d) => {
      if (!d) return d;
      const done = new Set(d.progress.layers_done || []);
      if (current) done.delete(idx); else done.add(idx);
      return { ...d, progress: { ...d.progress, layers_done: Array.from(done).sort((a, b) => a - b), status: d.progress.status === "not_started" ? "in_progress" : d.progress.status } };
    });
    try {
      await axios.post(`${API}/api/mastery/skills/${skillId}/layer`, { layer_idx: idx, done: !current }, { withCredentials: true });
    } catch {
      toast.error("Couldn't save progress — please try again.");
      await load();
    }
  };

  const markMastered = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/api/mastery/skills/${skillId}/master`, {}, { withCredentials: true });
      toast.success(`${data.skill.title} — mastered properly. Well done.`);
      await load();
      if (res.suggested_next) {
        setTimeout(() => {
          toast("Ready for the next skill when you are.", {
            action: { label: `Go to ${res.suggested_next.title}`, onClick: () => nav(`/instructor/mastery/${res.suggested_next.id}`) },
          });
        }, 600);
      }
    } finally { setBusy(false); }
  };

  const agreeNext = async (nextId) => {
    try {
      await axios.post(`${API}/api/mastery/skills/${skillId}/agree-next`, { next_skill_id: nextId }, { withCredentials: true });
      toast.success("Next skill agreed.");
      if (nextId) nav(`/instructor/mastery/${nextId}`);
      else await load();
    } catch {
      toast.error("Couldn't save.");
    }
  };

  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  const s = data.skill;
  const p = data.progress;
  const layersDone = new Set(p.layers_done || []);
  const mastered = p.status === "mastered";
  const notStarted = p.status === "not_started";
  const agreedNext = p.next_skill_agreed;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-24">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="mastery-skill-back-btn"
            onClick={() => nav("/instructor/mastery")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 data-testid="mastery-skill-title" className="text-base font-extrabold text-slate-900 dark:text-white truncate">{s.title}</h1>
            <p className="text-[11px] text-slate-500 truncate">{s.summary}</p>
          </div>
          {mastered && (
            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-600 text-white flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Mastered
            </span>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Principle */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/10 border border-indigo-100 dark:border-indigo-900/40 p-3.5">
          <p className="text-xs text-indigo-900 dark:text-indigo-100 leading-snug flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-600 dark:text-indigo-300" />
            <span>
              <span className="font-extrabold">Slow is control.</span> We are not going backwards —
              we are mastering the skill properly.
            </span>
          </p>
        </div>

        <SectionCard testid="section-start" icon={<Compass className="w-4 h-4" />} label="Best starting point">
          {s.best_starting_point}
        </SectionCard>

        <SectionCard testid="section-outcome" icon={<Target className="w-4 h-4" />} label="Today's single outcome">
          {s.todays_single_outcome}
        </SectionCard>

        <SectionCard testid="section-slow" icon={<Timer className="w-4 h-4" />} label="Slow practice method">
          {s.slow_practice_method}
        </SectionCard>

        {/* Layered progression — the interactive core */}
        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-slate-500" />
            <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500">Layered progression</p>
          </div>
          <ol data-testid="mastery-layers" className="space-y-2">
            {s.layered_progression.map((layer, i) => {
              const isDone = layersDone.has(i);
              return (
                <li key={i}>
                  <button
                    data-testid={`mastery-layer-${i}`}
                    onClick={() => toggleLayer(i, isDone)}
                    disabled={notStarted || mastered}
                    className={`w-full text-left flex items-start gap-2.5 rounded-xl border p-2.5 transition-all ${
                      isDone
                        ? "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40"
                        : "bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 hover:border-slate-300"
                    } disabled:opacity-75 disabled:cursor-not-allowed`}
                  >
                    {isDone ? (
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 mt-0.5 text-slate-300 flex-shrink-0" />
                    )}
                    <span className={`text-[13px] leading-snug ${isDone ? "text-emerald-900 dark:text-emerald-100" : "text-slate-700 dark:text-slate-300"}`}>
                      <span className="font-bold">Layer {i + 1}.</span> {layer}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          {notStarted && (
            <button
              data-testid="mastery-start-btn"
              onClick={startSkill}
              disabled={busy}
              className="mt-3 w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {busy ? "Starting…" : "Start this skill"}
            </button>
          )}
        </div>

        <SectionCard testid="section-faults" icon={<AlertCircle className="w-4 h-4" />} label="Common faults to watch for" tone="amber">
          <ul className="space-y-1 text-[13px]">
            {s.common_faults.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard testid="section-check" icon={<CheckCircle2 className="w-4 h-4" />} label="Mastery check" tone="emerald">
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mb-2">All of these should feel natural — not forced.</p>
          <ul className="space-y-1.5 text-[13px]">
            {s.mastery_check.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        {!mastered && (
          <button
            data-testid="mastery-mark-mastered"
            onClick={markMastered}
            disabled={busy || notStarted}
            className="w-full px-4 py-3 text-sm font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Trophy className="w-4 h-4" /> Mark as mastered
          </button>
        )}

        {/* Agree next skill */}
        {data.suggested_next && (
          <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
            <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-1">Agree next skill</p>
            <p className="text-[11px] text-slate-400 mb-3">
              Suggested next: <span className="font-bold text-slate-700 dark:text-slate-300">{data.suggested_next.title}</span> · {data.suggested_next.summary}
            </p>
            <button
              data-testid="mastery-agree-next"
              onClick={() => agreeNext(data.suggested_next.id)}
              className={`w-full px-4 py-2.5 text-sm font-extrabold rounded-xl flex items-center justify-center gap-2 ${
                agreedNext === data.suggested_next.id
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
            >
              {agreedNext === data.suggested_next.id ? "✓ Agreed — open now" : `Agree next: ${data.suggested_next.title}`}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCard({ testid, icon, label, tone = "slate", children }) {
  const tones = {
    slate: "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700",
    amber: "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40",
    emerald: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 mb-1.5 text-slate-500 dark:text-slate-400">
        {icon}
        <p className="text-xs font-extrabold uppercase tracking-wider">{label}</p>
      </div>
      <div className="text-[13px] text-slate-700 dark:text-slate-200 leading-snug">{children}</div>
    </div>
  );
}
